import { Action } from '../action/action';
import { on } from '../render/utils/dom';
const isMacOS =
  typeof navigator !== 'undefined' && navigator.platform
    ? navigator.platform.toUpperCase().indexOf('MAC') >= 0
    : false;

export class ActionProvider {
  constructor() {
    this.el = null;
    this.actions = [];
    this.stageContext = null;
    this.isShiftKeyPressed = false;
    this.isAltKeyPressed = false;
    this.isSpaceKeyPressed = false;
    this.isMetaKeyPressed = false;
    this.stageContext = null;

    this.disposeCallbacks = [];
  }

  attach(stageContext) {
    this.stageContext = stageContext;
    const el = stageContext.canvas.interactive.parentNode;
    el.tabIndex = '-1';
    this.el = el;

    // Unified key state handler
    const handleKeyState = (event, isKeyDown) => {
      const key = event.key || event.code;
      switch (key) {
        case 'Shift':
          this.isShiftKeyPressed = isKeyDown;
          break;
        case 'Alt':
          this.isAltKeyPressed = isKeyDown;
          break;
        case 'Space':
        case ' ':
          this.isSpaceKeyPressed = isKeyDown;
          break;
        case 'Meta':
          this.isMetaKeyPressed = isKeyDown;
          break;
      }
    };

    const keyDownListener = (event) => {
      this.matchShortcut(event)?.run(this.stageContext);
      if (event.key === 'Tab') {
        event.preventDefault();
      }
      handleKeyState(event, true);
    };

    const keyUpListener = (event) => {
      handleKeyState(event, false);
      updatePressedStates(event);
      if (!event.code?.includes('Space')) this.isSpaceKeyPressed = false;
    };

    const updatePressedStates = (event) => {
      this.isShiftKeyPressed = event.shiftKey;
      this.isAltKeyPressed = event.altKey;
      this.isMetaKeyPressed = event.metaKey;
    };

    const onPaste = (e) => {
      if (!this.isFocused) return;
      const action = this.actions.find((action) => action.name === 'Paste');
      action.handler(this.stageContext, false, e);
    };

    this.stageContext = stageContext;
    this.disposeCallbacks.push(
      on(el, 'keydown', keyDownListener, false),
      on(el, 'keyup', keyUpListener, false),
      on(document, 'mousemove', updatePressedStates, false),
      on(document, 'keyup', keyUpListener, false),
      on(document, 'paste', onPaste)
    );
  }

  dispose() {
    this.disposeCallbacks.forEach((dispose) => dispose());
    this.disposeCallbacks = [];

    // Reset all key states when disposing
    this.isShiftKeyPressed = false;
    this.isAltKeyPressed = false;
    this.isSpaceKeyPressed = false;
    this.isMetaKeyPressed = false;
  }

  add(action) {
    if (!(action instanceof Action)) return;
    const exist = this.actions.find((a) => !!a.shortcuts && a.shortcuts === action.shortcuts);
    if (exist) {
      throw new Error(`Action with shortcuts ${action.shortcuts} already exists`);
    }
    this.actions.push(action);
  }

  getActions() {
    return this.actions;
  }

  get isFocused() {
    const el = this.el;
    if (this.stageContext.providers.text?.currentNode) {
      return false;
    }
    if (document.activeElement.shadowRoot) {
      return document.activeElement.shadowRoot.activeElement === el;
    } else {
      return document.activeElement === el;
    }
  }

  matchShortcut(event) {
    if (!this.isFocused) {
      return null;
    }
    const shortcuts = new Set();
    const command = isMacOS ? (event.metaKey ? 'Ctrl' : '') : event.ctrlKey ? 'Ctrl' : '';
    const alt = event.altKey ? 'Alt' : '';
    const shift = event.shiftKey ? 'Shift' : '';
    if (command) {
      shortcuts.add('CTRL');
    }
    if (alt) {
      shortcuts.add('ALT');
    }
    if (shift) {
      shortcuts.add('SHIFT');
    }
    const key = event.key.toUpperCase();
    key !== 'CONTROL' && key !== 'ALT' && key !== 'SHIFT' && shortcuts.add(key);
    const action = this.actions.find(
      (action) => !action.isDisabled(this.stageContext) && action.match(shortcuts)
    );
    if (action) {
      event.preventDefault();
      return action;
    }
    return null;
  }
}
